// routes/components/EntryRedirect.tsx
/**
 * EntryRedirect - Wraps the staff and player dashboard entry points
 * (/landing and /player). Bumps a logged-in user with no remembered
 * picker preference to /welcome so they get the staff-vs-player
 * choice, then renders the actual page once a preference is set.
 *
 * Sits inside the existing auth wrapper, so it never sees an
 * unauthenticated user. Only entry points use this - deep-linked
 * sub-routes (e.g. /player/decks/abc) skip it.
 *
 * Forwards the `user` prop down to the page element. The auth
 * wrappers (ProtectedRoute / PlayerProtectedRoute) inject `user`
 * via cloneElement; without re-injecting here that prop would stop
 * at EntryRedirect and the page would render with user=undefined,
 * which breaks PageContainer / NavMenu.
 */

import { ReactElement, cloneElement, isValidElement } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PageLoading } from '../../components/common/Loading';
import { ROUTES } from '../../config/routes';
import { readWelcomePreference } from '../../features/auth/pages/WelcomePage';

export interface EntryRedirectProps {
  children: ReactElement;
}

export function EntryRedirect({ children }: EntryRedirectProps): ReactElement {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoading />;
  }

  if (user && !readWelcomePreference()) {
    return <Navigate to={ROUTES.WELCOME} replace />;
  }

  if (isValidElement(children)) {
    return cloneElement(children, { user } as { user: typeof user });
  }

  return children;
}

export default EntryRedirect;
